<template>
  <section
    class="border bg-slate-50 transition-[border-color,box-shadow] duration-700 dark:bg-slate-900"
    :style="cardStyle"
  >
    <div class="flex items-center justify-between border-b border-slate-200 px-4 pb-2.5 pt-3 dark:border-slate-800">
      <span class="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{{ workspaceSlug }}</span>
      <span class="shrink-0 font-mono text-xs text-slate-500 dark:text-slate-400">{{ meterName }}</span>
    </div>

    <div class="px-4 pb-5 pt-4">
      <div class="flex items-center gap-2.5">
        <span class="text-2xl leading-none" aria-hidden="true">{{ health.emoji }}</span>
        <span class="text-xl font-extrabold tracking-tight" :style="{ color: stateColor }">
          {{ health.label }}
        </span>
      </div>
      <p class="mt-1 text-sm text-slate-700 dark:text-slate-300">{{ health.note }}</p>

      <div class="mt-4 flex gap-1" aria-hidden="true">
        <span
          v-for="segment in SEGMENT_COUNT"
          :key="segment"
          class="h-3 flex-1 transition-colors duration-700"
          :style="segmentStyle(segment)"
        ></span>
      </div>
      <div class="mt-2 flex justify-between font-mono text-xs text-slate-500 dark:text-slate-400" aria-hidden="true">
        <span>{{ lowEndpoint }}</span>
        <span>{{ highEndpoint }}</span>
      </div>

      <p
        v-if="health.open_under_load"
        class="mt-3 border-l-2 border-amber-400 pl-2 text-xs text-amber-800 dark:text-amber-300"
      >
        {{ health.counts.open_in_play }} tasks are in play. You can add more, but closing one first will lower the load.
      </p>

      <div v-if="nudge" class="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <p class="text-sm text-slate-700 dark:text-slate-300">{{ nudge.sentence }}</p>

        <button
          v-if="nudge.task"
          type="button"
          class="mt-3 block w-full border border-slate-200 bg-white px-3 py-2 text-left hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-slate-600"
          @click="openTask"
        >
          <span class="block font-mono text-xs text-slate-500 dark:text-slate-400">{{ nudge.task.ref }}</span>
          <span class="mt-0.5 block truncate text-sm font-semibold text-slate-950 dark:text-slate-100">{{ nudge.task.title }}</span>
        </button>

        <div v-if="nudge.primary_action || nudge.secondary_action" class="mt-3 flex flex-wrap gap-2">
          <button
            v-if="nudge.primary_action"
            type="button"
            class="h-8 border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            :disabled="submitting"
            @click="performAction(nudge.primary_action)"
          >
            {{ nudge.primary_action.label }}
          </button>
          <button
            v-if="nudge.secondary_action"
            type="button"
            class="h-8 px-2 text-xs font-semibold text-slate-500 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-400 dark:hover:text-slate-100"
            :disabled="submitting"
            @click="performAction(nudge.secondary_action)"
          >
            {{ nudge.secondary_action.label }}
          </button>
        </div>

        <GutScorePicker
          v-if="showScores"
          class="mt-3"
          :disabled="submitting"
          @pick="parkTask"
        />

        <p
          v-if="message"
          class="mt-2 text-xs"
          :class="messageToneClass"
          role="status"
        >
          {{ message }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useApi } from '../../composables/useApi'
import GutScorePicker from './GutScorePicker.vue'

const SEGMENT_COUNT = 12
const OPEN_SOFT_CAP = 5
const COLLECTING_FILLED_SEGMENTS = {
  growing: 11,
  ones_burning: 8,
  cooling: 4,
  graveyard: 2,
}

const props = defineProps({
  health: {
    type: Object,
    required: true,
  },
  store: {
    type: Object,
    required: true,
  },
  workspaceSlug: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['changed'])
const api = useApi()
const showScores = ref(false)
const submitting = ref(false)
const message = ref('')
const messageTone = ref('ok')

const nudge = computed(() => props.health.nudge || null)
const stateColor = computed(() => props.health.color_bright || props.health.color)
const isCollecting = computed(() => props.health.mode === 'collecting')
const meterName = computed(() => isCollecting.value ? 'greenhouse' : 'ship-o-meter')
const lowEndpoint = computed(() => isCollecting.value ? '🪦 graveyard' : 'calm')
const highEndpoint = computed(() => isCollecting.value ? 'growing 🌱' : 'overloaded')
const pressure = computed(() => Math.min(1, Number(props.health.counts?.open_in_play || 0) / OPEN_SOFT_CAP))
const filled = computed(() => {
  if (isCollecting.value) return COLLECTING_FILLED_SEGMENTS[props.health.state] || 0

  return Math.round(pressure.value * SEGMENT_COUNT)
})
const cardStyle = computed(() => ({
  borderColor: `color-mix(in oklab, ${props.health.color} 35%, transparent)`,
  boxShadow: `0 0 40px -14px color-mix(in oklab, ${props.health.color} 55%, transparent)`,
}))
const messageToneClass = computed(() => {
  return messageTone.value === 'error'
    ? 'text-red-700 dark:text-red-300'
    : 'text-slate-500 dark:text-slate-400'
})

watch(
  () => [nudge.value?.kind, nudge.value?.task?.id],
  () => {
    showScores.value = false
    message.value = ''
    messageTone.value = 'ok'
  }
)

function segmentStyle(segment) {
  return segment <= filled.value
    ? { backgroundColor: props.health.color }
    : { backgroundColor: 'color-mix(in oklab, currentColor 12%, transparent)' }
}

function openTask() {
  if (!nudge.value?.task?.id) return

  props.store.openTask(nudge.value.task.id)
}

async function performAction(action) {
  if (!action || submitting.value) return

  if (action.type === 'open_task') {
    openTask()
    return
  }

  if (action.type === 'park_task') {
    showScores.value = !showScores.value
    return
  }

  submitting.value = true
  message.value = ''
  messageTone.value = 'ok'

  try {
    if (action.type === 'start_task') {
      await props.store.updateTask(nudge.value.task.id, { status: 'in_progress' })
    } else if (action.type === 'bury_corpses') {
      await api.buryCorpses(props.workspaceSlug)
      message.value = 'Into oblivion. They had a good run.'
    } else if (action.type === 'set_ship_candidate') {
      await api.setShipCandidate(props.workspaceSlug, nudge.value.task.id)
    } else if (action.type === 'dismiss_sell') {
      await api.dismissSellNudge(props.workspaceSlug)
    }

    emit('changed')
  } catch (error) {
    messageTone.value = 'error'
    message.value = actionError(error, action.type)
  } finally {
    submitting.value = false
  }
}

async function parkTask(score) {
  if (!nudge.value?.task?.id || submitting.value) return

  submitting.value = true
  message.value = ''
  messageTone.value = 'ok'

  try {
    await props.store.updateTask(nudge.value.task.id, {
      planning_state: 'parked',
      gut_score: score,
      status: 'backlog',
    })
    showScores.value = false
    message.value = 'Parked. Sleep well.'
    emit('changed')
  } catch (error) {
    messageTone.value = 'error'
    message.value = error?.status === 423 ? 'Task is locked' : (error?.message || 'Could not park task.')
  } finally {
    submitting.value = false
  }
}

function actionError(error, actionType) {
  if (error?.status === 423) return 'Task is locked'

  return error?.message || {
    start_task: 'Could not start task.',
    bury_corpses: 'Could not bury ideas.',
    set_ship_candidate: 'Could not name the release slice.',
    dismiss_sell: 'Could not dismiss the sell nudge.',
  }[actionType] || 'Could not complete the action.'
}
</script>
